<template>
  <div class="order-step">
    <next-prev-menu
      :previous="false"
      title="Naar Bezorgen"
      @next-step="$router.push({ name: 'printOrderDeliver' })"
    ></next-prev-menu>
    <div id="print-specs" class="two-col-res" :class="{ flipped: flipped }">
      <div>
        <div class="card">
          <div id="scroll-view" class="card-default">
            <div class="select-all-container">
              <select-box
                :selected="
                  selectedFiles.length === fileList.length &&
                  fileList.length > 0
                "
                @action="selectAll()"
                v-if="fileList.length > 0"
              ></select-box>
              <h3 @click="selectAll()">Alles selecteren</h3>

              <h3 class="select-all-title" @click="selectAll()">
                {{ selectAllText }}
              </h3>
            </div>
            <div id="scroll-list" class="selection-list selectable">
              <p class="error-text empty-message" v-show="totalFiles === 0">
                <span class="empty-dt"
                  >Sleep uw bestanden in de bol of klik op de +</span
                >
                <span class="empty-mb"
                  >Klik op de + om bestanden toe te voegen</span
                >
              </p>
              <ul
                class="card-list file-list"
                :class="{ 'overflow-correction': fileList.length > 3 }"
              >
                <li v-for="file in fileList" :key="file.uuid">
                  <list-item
                    :file="file"
                    :hide-specs="hideSpecs"
                    @select-box="selectFile(file, 'multi')"
                    @click.native="selectFile(file, 'single')"
                    @input-select="inputSelect(file)"
                    @remove="clearFile(file)"
                    @set-qt="setQuantity($event)"
                    :selected="selectedFileNames.indexOf(file.fileName) > -1"
                    @set-selectable="setSelectable"
                  ></list-item>
                </li>
              </ul>
              <ul class="card-list file-list">
                <li
                  v-for="(product, index) in printStoreProducts"
                  :key="index"
                  class="product"
                >
                  <print-store-product
                    @set-qt="setPrintStoreQuantity($event, index)"
                    :product="product"
                    @remove="removeProduct(product)"
                  ></print-store-product>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>

      <div>
        <div class="card card-front sub-card">
          <div class="card-default">
            <p
              v-if="selectedFileNames.length < 1"
              class="placeholder specs-placeholder"
            >
              Selecteer een bestand om de<br />specificaties aan te passen.
            </p>
            <div v-else-if="!hideSpecs">
              <tabs
                class="tabs-header"
                :active-tab="specsMenuType === 'products' ? 'right' : 'left'"
              >
                <template #left-tab>
                  <div
                    v-if="selectedProduct"
                    class="dual-tab-button"
                    :class="{ active: specsMenuType === 'productSpecs' }"
                  >
                    <button @click="setProduct(selectedProduct)">
                      <span>{{ selectedProduct }}</span>
                    </button>
                    <close-button
                      class="close-btn"
                      v-if="specsMenuType === 'productSpecs'"
                      @click="resetFile"
                    />
                  </div>
                  <button class="tab-btn" @click="setSpecsMenu('custom')">
                    Op maat
                  </button>
                </template>

                <template #right-tab>
                  <div
                    v-if="!hideTemplates"
                    class="dual-tab-button"
                    :class="{ active: specsMenuType === 'products' }"
                  >
                    <button @click="setSpecsMenu('products')">
                      <span>Templates</span>
                    </button>

                    <button
                      @click="toggleSaveTemplate"
                      v-if="specsMenuType === 'products'"
                      class="save-template-button"
                    >
                      <div class="add-button">
                        <span></span>
                        <span></span>
                      </div>
                    </button>
                  </div>
                </template>
              </tabs>

              <save-template-panel
                class="save-template-panel"
                v-if="showSaveTemplate"
                @close="closeSaveTemplate"
                @saved="hydrateTemplates"
                :files="selectedFiles"
              ></save-template-panel>
              <div class="header-space"></div>

              <print-specs
                v-if="specsMenuType === 'custom'"
                :full-specs="customSpecs"
                :selected-files="selectedFiles"
                @showPaperTypes="showPaperTypes"
                @setPaperTypes="setPaperTypes"
                @setCurPaperType="setCurPaperType"
                @showTemplates="flipCard('templates')"
              ></print-specs>
              <product-list
                v-else-if="specsMenuType === 'products'"
                :products="products"
                :templates="printTemplates"
                :files="selectedFiles"
                :secure="secure"
                @setProduct="setProduct"
                @reset="hydrateTemplates"
                @clear="resetFile"
              ></product-list>
              <product-specs-menu
                v-else-if="specsMenuType === 'productSpecs'"
                @close="flipCard('specs')"
                :productSpecs="selectedProductSpecs"
                :activeSpecs="activeProductSpecs"
                :selectedFiles="selectedFileNames"
              ></product-specs-menu>
            </div>
            <div class="sub-btn-holder">
              <button
                class="main-button"
                :class="{ active: orderDetails.instruction.length > 0 }"
                @click="flipCard('instructions')"
              >
                Extra instructies voor printshop
              </button>
            </div>
          </div>
        </div>

        <div class="card card-back instruction-card sub-card">
          <div v-if="cardBack === 'instructions'" class="card-default">
            <instructions
              :instruction="orderDetails.instruction"
              :hide-specs="hideSpecs"
              :active="flipped"
              @update="setInstruction"
              @close="flipCard('specs')"
            ></instructions>
          </div>
          <div v-else-if="cardBack === 'paperType'" class="card-default">
            <paper-type-menu
              @close="flipCard('specs')"
              @remove="setPaperType"
              @setPaperType="setPaperType"
              :paperType="paperType"
              :paperTypes="paperTypes"
            ></paper-type-menu>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import isPlainObject from 'lodash-es/isPlainObject'
import { mapStores } from 'pinia'
import { usePrintStoreStore } from '@/features/printstore/PrintStore.store'
import { usePrintOrderStore } from '@/features/printOrder/PrintOrder.store'

import ListItem from './ListItem.vue'
import PaperTypeMenu from './cart/PaperTypeMenu.vue'
import { printTemplates } from '../PrintOrderApi'
import Instructions from './cart/Instructions.vue'
import PrintSpecs from './cart/PrintSpecs.vue'
import ProductList from './cart/ProductList.vue'
import ProductSpecsMenu from './cart/ProductSpecs.vue'
//printStore
import PrintStoreProduct from './printstore/Product.vue'
import cloneDeep from 'lodash-es/cloneDeep'
import { sortBy } from 'lodash-es/collection'
import Tabs from '../../../components/Tabs.vue'
import SaveTemplatePanel from './cart/SaveTemplatePanel.vue'
import NextPrevMenu from '../../../components/NextPrevMenu.vue'
import { useFileListStore } from '@/stores/FileList.store'

export default {
  props: {},
  data() {
    return {
      cardBack: '',
      customSpecs: {},
      productSpecs: {},
      products: [],
      flipped: false,
      //hideSpecs: false,
      paperType: '',
      paperTypes: [],
      paperTypeFallback: '',
      selectedFiles: [],
      specsMenuType: '',
      selectedProductSpecs: {},
      activeProductSpecs: {},
      printTemplates: {},
      showSaveTemplate: false,
      selectable: true
    }
  },
  components: {
    ListItem,
    PaperTypeMenu,
    Instructions,
    PrintSpecs,
    ProductList,
    ProductSpecsMenu,
    PrintStoreProduct,
    Tabs,
    SaveTemplatePanel,
    NextPrevMenu
  },
  computed: {
    ...mapStores(usePrintStoreStore, useFileListStore),
    printStoreProducts() {
      return sortBy(this.printStoreStore.orderDetails.cart, ['fileName'])
    },
    orderDetails() {
      return this.printOrderStore.orderDetails
    },
    secure() {
      return this.printOrderStore.orderDetails.secure
    },
    fileList() {
      return this.fileListStore.fileList
    },
    printOrderStore() {
      return usePrintOrderStore()
    },
    hideTemplates() {
      const userRoles = this.rootStore.user.roles
        ? this.rootStore.user.roles
        : []
      if (this.rootStore.theme.customFields.hideTemplates === 'always') {
        return true
      } else if (
        this.rootStore.theme.customFields.hideTemplates === 'USER_PRINTSPECS' &&
        userRoles.indexOf('USER_PRINTSPECS') > -1
      ) {
        return true
      } else {
        return false
      }
    },
    hideSpecs() {
      if (
        this.rootStore.theme.customFields.printSpecs.hide ||
        this.rootStore.interaction.hideSpecs
      ) {
        return true
      } else {
        return false
      }
    },
    selectedProduct() {
      let products = []
      this.selectedFiles.forEach((file) => {
        if (file.productSpecs.title) {
          if (products.indexOf(file.productSpecs.title) < 0) {
            products.push(file.productSpecs.title)
          }
        } else {
          if (products.indexOf('') < 0) {
            products.push('')
          }
        }
      })

      return products.length === 1 ? products[0] : ''
    },
    selectedFileNames() {
      let fileNames = []
      this.selectedFiles.forEach((file) => {
        fileNames.push(file.fileName)
      })

      return fileNames
    },
    selectedTemplates() {
      let templates = []
      this.selectedFiles.forEach((file) => {
        if (templates.indexOf(file.template) < 0) {
          templates.push(file.template)
        }
      })

      return templates
    },
    selectAllText() {
      if (this.fileList.length === 0 && this.printStoreProducts.length > 0) {
        return 'Producten'
      } /* else if (
        this.fileList.length === 0 &&
        this.printStoreProducts.length > 1
      ) {
        return this.printStoreProducts.length + ' producten'
      } */ else if (
        this.selectedFiles.length === this.fileList.length &&
        this.fileList.length > 1 &&
        this.printStoreProducts.length === 0
      ) {
        return 'Alle ' + this.fileList.length + ' bestanden geselecteerd'
      } else if (
        this.selectedFiles.length === this.fileList.length &&
        this.fileList.length === 1 &&
        this.printStoreProducts.length === 0
      ) {
        return '1 bestand geselecteerd'
      } else if (
        this.selectedFiles.length > 0 ||
        this.printStoreProducts.length > 0
      ) {
        return (
          this.selectedFiles.length +
          ' van de ' +
          this.totalFiles +
          ' bestanden geselecteerd'
        )
      } else if (this.selectedFiles.length === 0 && this.totalFiles === 1) {
        return '1 bestand'
      } else if (this.selectedFiles.length === 0 && this.totalFiles > 1) {
        return this.totalFiles + ' bestanden'
      } else {
        return 'Bestanden'
      }
    },
    totalFiles() {
      return this.fileList.length + this.printStoreProducts.length
    }
  },
  async created() {
    if (this.hideSpecs) {
      this.flipCard('instructions')
    }
    let customSpecs = {}
    if (this.secure) {
      customSpecs = this.printOrderStore.printSpecsSecure
    } else {
      customSpecs = this.printOrderStore.printSpecs
    }

    const specKeys = Object.keys(customSpecs)

    specKeys.forEach((spec) => {
      if (isPlainObject(customSpecs[spec].paperType)) {
        customSpecs[spec].paperType = Object.keys(customSpecs[spec].paperType)
      }
    })

    this.customSpecs = customSpecs

    //check if format is available in specs, if not set to available format
    let formats = Object.keys(customSpecs).filter(function (fm) {
      return fm !== 'Als bestand'
    })
    this.fileList.forEach((file) => {
      if (Object.keys(customSpecs).indexOf(file.format) === -1) {
        this.fileListStore.updateFileSpec(file.fileName, 'format', formats[0])
      }
    })

    if (this.secure) {
      this.productSpecs = this.printOrderStore.printTemplatesSecure
      //secure templ
    } else {
      this.productSpecs = this.printOrderStore.printTemplates
      //normal templ
    }

    this.products = Object.keys(this.productSpecs)

    printTemplates().then((resp) => this.hydrateTemplates(resp))
  },
  mounted() {
    if (this.fileList.length > 0) {
      setTimeout(() => {
        this.selectFile(this.fileList[0], 'single')
      }, 1000)
    }
  },
  methods: {
    setSelectable(val) {
      setTimeout(() => {
        this.selectable = val
      }, 500)
    },
    setSpecsMenu(type) {
      if (type === 'custom') {
        this.fileListStore.addProduct([this.selectedFileNames, {}])
      }
      this.specsMenuType = type
    },
    setSpecsType(type) {
      this.specsType = type
    },
    showPaperTypes(fb) {
      this.paperTypeFallback = fb
      this.cardBack = 'paperType'
      this.flipped = true
    },
    setPaperTypes(pt) {
      this.paperTypes = pt
    },
    setCurPaperType(pt) {
      this.paperType = pt
    },
    setInstruction(val) {
      this.printOrderStore.updateOrderDetails('instruction', val)
    },
    async clearFile(file) {
      // If file is not selected delete only this one
      // If file is selected delete all selected
      const fileIsSelected = this.selectedFiles.find(
        (selectedFile) => selectedFile.uuid === file.uuid
      )

      if (!fileIsSelected) {
        await this.fileListStore.removeFile(file.uuid)
      } else {
        let idsToRemove = []
        for (const file of this.selectedFiles) {
          idsToRemove.push(file.uuid)
        }
        await this.fileListStore.removeFiles(idsToRemove)

        this.selectedFiles = []
      }
    },
    flipCard(card) {
      if (this.hideSpecs) {
        card = 'instructions'
      }
      if (card === 'specs') {
        this.flipped = false
        //this.cardBack = ''
      } else {
        this.cardBack = card
        this.flipped = true
      }
    },
    inputSelect(file) {
      if (this.selectedFileNames.indexOf(file.fileName) === -1) {
        this.selectFile(file, 'input')
      }
    },
    selectAll() {
      if (this.selectedFiles.length === this.fileList.length) {
        this.selectedFiles = []
      } else {
        this.selectedFiles = []
        this.selectedFiles = [...this.fileList]
      }

      if (this.selectedFiles.length > 1) {
        if (this.selectedFiles.some((e) => e.productSpecs.title)) {
          //multiple selection, one of them has a product
          this.setSpecsMenu('products')
        }
      }

      this.flipCard('specs')
    },
    selectFile(file, type) {
      if (!this.selectable && type != 'input') {
        return
      }
      if (type === 'single' || type === 'input') {
        this.selectedFiles = []
      }

      if (!this.selectedFiles.some((fl) => fl.fileName === file.fileName)) {
        this.selectedFiles.push(file)
      } else {
        this.selectedFiles.splice(
          this.selectedFiles.findIndex((fl) => fl.fileName === file.fileName),
          1
        )
      }

      if (
        this.selectedFiles.length === 1 &&
        !this.selectedFiles[0].productSpecs.title
      ) {
        this.setSpecsMenu('custom')
      } else if (
        this.selectedFiles.length === 1 &&
        this.selectedFiles[0].productSpecs.title
      ) {
        this.setProduct(this.selectedFiles[0].productSpecs.title)
      } else if (this.selectedFiles.length > 1) {
        if (this.selectedFiles.some((e) => e.productSpecs.title)) {
          //multiple selection, one of them has a product
          this.setSpecsMenu('products')
        }
      }

      this.flipCard('specs')
    },
    setPaperType(val) {
      this.flipCard('specs')

      let fileNames = []
      this.selectedFiles.forEach((file) => {
        fileNames.push(file.fileName)
      })

      const pType = val || this.paperTypeFallback
      this.fileListStore.updateFileSpec(fileNames, 'template', '')

      this.fileListStore.updateFileSpec(fileNames, 'material', pType)

      this.fileListStore.updateFileSpec(fileNames, 'paperType', pType)
    },
    setQuantity(val) {
      this.fileListStore.updateFileSpec(this.selectedFileNames, 'quantity', val)
    },
    setPrintStoreQuantity(val, index) {
      const count = parseInt(val)
      let newProducts = cloneDeep(this.printStoreProducts)

      newProducts[index].quantity = count

      this.printStoreStore.updateCart(newProducts)
    },
    setProduct(product) {
      //set productspecs for specs menu
      this.selectedProductSpecs = {
        title: product,
        ...this.productSpecs[product]
      }

      // check if selected product is allready chosen product, if so, get current specs, else hydrate product object and set filespecs
      if (
        this.selectedFiles.length === 1 &&
        this.selectedFiles[0].productSpecs.title === product
      ) {
        //is allready product
        this.activeProductSpecs = this.selectedFiles[0].productSpecs
      } else {
        let newProduct = {}
        newProduct.title = product
        this.productSpecs[product].forEach((spec) => {
          newProduct[spec.title] = spec.default

          if (
            spec.type === 'option' &&
            spec.options[spec.default].submenus &&
            spec.options[spec.default].submenus.length > 0
          ) {
            spec.options[spec.default].submenus.forEach((opt) => {
              newProduct[opt.title] = opt.default
            })
          }

          this.activeProductSpecs = newProduct

          this.fileListStore.addProduct(this.selectedFileNames, newProduct)

          this.fileListStore.updateFileSpec(
            this.selectedFileNames,
            'template',
            ''
          )
        })
      }

      this.specsMenuType = 'productSpecs'

      //this.flipCard('productSpecs')
    },

    removeProduct(product) {
      this.printStoreStore.removeFromCart(product)
    },
    resetFile() {
      this.selectedProductSpecs = {}
      this.activeProductSpecs = {}

      this.setSpecsMenu('custom')
    },
    toggleSaveTemplate() {
      this.showSaveTemplate = !this.showSaveTemplate
    },
    closeSaveTemplate() {
      this.showSaveTemplate = false
    },
    hydrateTemplates(templates) {
      this.printTemplates = templates
    }
  }
}
</script>

<style lang="scss" scoped>
/* .order-step .two-col-res > :nth-child(2) {
  flex: 1 0 300px;
} */

.specs-header {
  padding-bottom: 15px;

  .toggle {
    display: flex;
    justify-content: space-between;
    button {
      width: 50%;
      text-align: center;
    }
  }
}

.tabs-header {
  position: absolute;
  top: 0;
  left: 0;
}

.select-all-container {
  display: flex;
  align-items: center;
}

.select-all {
  //margin-right: 4px;
  margin-left: auto;
  h3 {
    display: inline-block;
    padding-right: 5px;
  }
}

.select-all-ph {
  width: 21px;
}

.selection-list {
  border-bottom-left-radius: 5px;
  height: calc(100% - 10px);
  //height: 431px;
  margin: 5px -20px -20px;
  overflow-y: auto;
  perspective: 1px;
  overflow-x: hidden;
  padding-left: 15px;
  padding-top: 0;
  padding-bottom: 5px;

  ul {
    margin-bottom: 0;
  }
}

.header-space {
  margin-top: 27px;
}

.sub-btn-holder {
  margin-top: auto;
  margin-left: -6px;
  padding-top: 20px;
  margin-bottom: -7px;
}

.empty-message {
  padding: 6px;
}

.empty-dt {
  display: none;
}

.tab-btn {
  width: 100%;
  height: 100%;
  font-size: 10px;
  text-transform: uppercase;
  color: var(--sub-color);
  font-weight: 500;
  display: inline-block;
}

.dual-tab-button {
  width: 100%;
  height: 100%;
  //display: flex;
  //align-items: center;

  &.active span {
    color: var(--sub-color);
  }

  .close-btn {
    position: absolute;
    margin-left: -20px;
    margin-top: 10px;
  }

  .add-button {
    background: transparent;
    border: none;
    width: 10px;
    height: 10px;
    padding: 0;
    position: absolute;
    right: 29px;
    top: 11px;

    span {
      display: block;
      background-color: var(--sub-color);
      position: absolute;
    }

    span:first-child {
      width: 10px;
      height: 2px;
      left: 0;
      top: 4px;
    }

    span:nth-child(2) {
      height: 10px;
      width: 2px;
      left: 4px;
      top: 0;
    }
  }

  button:first-child {
    text-transform: uppercase;
    font-size: 10px;
    font-weight: 500;
    height: 100%;
    width: 100%;

    span {
      white-space: nowrap;
      text-overflow: ellipsis;
      overflow: hidden;
      display: inline-block;
      width: 100%;
    }
  }
}

.save-template-panel {
  position: absolute;
  z-index: 100;
  top: 35px;
  right: 0;
}

.file-list.overflow-correction {
  margin-right: 0;
}

.file-list li {
  padding: 5px 5px 0;
}

.select-all-title {
  cursor: pointer;
  margin-left: auto;
}

.order-step .two-col-res > :nth-child(2) {
  flex-basis: 300px;
}

@media only screen and (min-width: 620px) {
  .empty-dt {
    display: block;
  }

  .empty-mb {
    display: none;
  }
}
</style>
